import React, { useState } from 'react';
import {
  Box,
  Button,
  TextField,
  FormControlLabel,
  Checkbox,
  Typography,
  Alert,
  CircularProgress,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  FormHelperText,
  Collapse,
  Paper,
  DialogTitle,
  DialogContent,
  DialogActions,
  Dialog
} from '@mui/material';
import BugReportIcon from '@mui/icons-material/BugReport';
import SendIcon from '@mui/icons-material/Send';
import FolderIcon from '@mui/icons-material/Folder';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import EditIcon from '@mui/icons-material/Edit';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import SaveIcon from '@mui/icons-material/Save';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import CloseIcon from '@mui/icons-material/Close';
import logCollectionService from '../../services/LogCollectionService';
import { useNavigate } from 'react-router-dom';
import PathSelectorDialog from '../dialogs/PathSelectorDialog';
import VideoBrowserDialog from '../dialogs/VideoBrowserDialog';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { getLanguagesForCreateVideo, SelectLanguageComponent } from '../subtitles/SelectLanguageComponent';

const BugReportForm = ({ onSubmitSuccess }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    title: '',
    steps: '',
    includeLogs: true,
    category: '',
    // Context-specific fields
    folderName: '',
    parentFolder: '',
    googleDriveLink: '',
    languages: '',
    videoName: '',
    folderPath: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [openBrowserDialog, setOpenBrowserDialog] = useState(false);
  const [videoBrowserOpen, setVideoBrowserOpen] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [selectedPath, setSelectedPath] = useState('');
  const [selectedVideos, setSelectedVideos] = useState([]); // For handling multiple video selection

  // Define bug categories
  const bugCategories = [
    { value: 'creating_folder', label: 'Creating a folder', icon: <FolderIcon fontSize="small" /> },
    { value: 'creating_video', label: 'Creating a video', icon: <VideoLibraryIcon fontSize="small" /> },
    { value: 'editing_video', label: 'Editing a video', icon: <EditIcon fontSize="small" /> },
    { value: 'exporting_file', label: 'Exporting a file', icon: <ImportExportIcon fontSize="small" /> },
    { value: 'importing_file', label: 'Importing a file', icon: <ImportExportIcon fontSize="small" /> },
    { value: 'saving_file', label: 'Saving a file', icon: <SaveIcon fontSize="small" /> },
    { value: 'other', label: 'Other issue', icon: <BugReportIcon fontSize="small" /> }
  ];

  const handleChange = (e) => {
    const { name, value, checked, type } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  // Add this function to handle navigation to video
  const navigateToVideo = () => {
    if (!formData.folderPath || !formData.videoName) {
      setSubmitStatus({
        success: false,
        message: 'Missing folder path or video name information'
      });
      return;
    }
    
    try {
      // Parse folder path to get the video ID
      // Format could be something like "Root/Folder1/Folder2/VideoName" or similar
      const pathParts = formData.folderPath.split('/').filter(part => part.trim() !== '');
      const videoId = formData.videoId || ''; // Use video ID if available
      
      if (videoId) {
        // If we have the video ID, navigate directly
        navigate(`/editor/${videoId}`);
      } else {
        // Otherwise navigate to the folder and let user find the video
        const folderId = pathParts[pathParts.length - 1] || '';
        navigate(`/folder/${folderId}`);
      }
    } catch (error) {
      setSubmitStatus({
        success: false,
        message: `Navigation error: ${error.message}`
      });
    }
  };

  // Update the handleSubmit function to include bugLanguages in context data

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Disable browser's default validation
    const form = e.target;
    const isValid = form.checkValidity();

    if (!formData.title || !formData.category) {
      setSubmitStatus({ 
        success: false, 
        message: 'Please fill in all required fields (title and category)' 
      });
      return;
    }
    
    // Validate context-specific fields based on category
    if (formData.category === 'creating_folder' && (!formData.folderName || !formData.parentFolder)) {
      setSubmitStatus({
        success: false,
        message: 'Please provide both folder name and parent folder'
      });
      return;
    }

    if (formData.category === 'creating_video' && (!formData.googleDriveLink || !formData.languages)) {
      setSubmitStatus({
        success: false,
        message: 'Please provide Google Drive link and languages'
      });
      return;
    }

    if (['editing_video', 'exporting_file', 'importing_file', 'saving_file'].includes(formData.category)) {
      const missingFields = [];
      if (!formData.videoName) missingFields.push('video name');
      if (!formData.folderPath) missingFields.push('folder path');
      if (!formData.languages) missingFields.push('languages');
      
      if (missingFields.length > 0) {
        setSubmitStatus({
          success: false,
          message: `Please provide ${missingFields.join(', ')}${missingFields.length > 1 ? ' fields' : ' field'}`
        });
        return;
      }
    }
    
    setIsSubmitting(true);
    
    try {
      // Create JSON object for context information instead of plain text
      let contextData = {};
      
      // Add basic fields to context data
      contextData.category = formData.category;
      
      // Add bugLanguages as a separate field in context data
      contextData.bugLanguages = formData.languages;
      
      // Add category-specific fields
      switch(formData.category) {
        case 'creating_folder':
          contextData.folderName = formData.folderName;
          contextData.parentFolder = formData.parentFolder;
          break;
          
        case 'creating_video':
          contextData.googleDriveLink = formData.googleDriveLink;
          contextData.languages = formData.languages;
          break;
          
        case 'editing_video':
        case 'exporting_file':
        case 'importing_file':
        case 'saving_file':
          // Handle multiple videos or single video
          if (Array.isArray(selectedVideos) && selectedVideos.length > 0) {
            contextData.videos = selectedVideos.map(video => {
              // Extract languages correctly based on available properties
              let videoLanguages = [];
              
              if (video.languages) {
                if (Array.isArray(video.languages)) {
                  videoLanguages = video.languages.map(lang => {
                    return typeof lang === 'string' ? lang : (lang.name || lang.languageName || '');
                  });
                } else if (typeof video.languages === 'string') {
                  videoLanguages = video.languages.split(',').map(l => l.trim());
                }
              } else if (video.videoLanguages) {
                if (Array.isArray(video.videoLanguages)) {
                  videoLanguages = video.videoLanguages.map(lang => {
                    return typeof lang === 'string' ? lang : (lang.name || lang.languageName || '');
                  });
                }
              }
              
              return {
                name: video.name,
                id: video.videoId || video._id || video.id || '',
                path: video.folderPath || video.path || '',
                languages: videoLanguages
              };
            });
          } else if (selectedVideo) {
            contextData.videoName = formData.videoName;
            contextData.videoId = formData.videoId;
            contextData.folderPath = formData.folderPath;
            contextData.languages = formData.languages;
          } else {
            // Manually entered video info
            contextData.videoName = formData.videoName;
            contextData.videoId = formData.videoId || '';
            contextData.folderPath = formData.folderPath;
            contextData.languages = formData.languages;
          }
          break;
          
        default:
          // No additional context for other categories
      }

      // Add system information in a readable format
      const contextSystemInfo = getSystemInfo();
      let systemInfo = `OS: ${contextSystemInfo.os || contextSystemInfo.platform}\n`;
      systemInfo += `Screen Resolution: ${contextSystemInfo.screenWidth}x${contextSystemInfo.screenHeight} (${contextSystemInfo.pixelRatio}x)\n`;
      systemInfo += `Window Size: ${contextSystemInfo.windowWidth}x${contextSystemInfo.windowHeight}\n`;
      systemInfo += `Timezone: ${contextSystemInfo.timezone}\n\n`;
      
      // Add languages information to system info
      if (formData.languages) {
        systemInfo += `Languages: ${formData.languages}\n\n`;
      }
      
      // Add JSON context data as a string
      let description = "";
      description += JSON.stringify(contextData, null, 2);
      
      const bugReport = {
        title: formData.title,
        description: description,
        steps: formData.steps,
        includeLogs: formData.includeLogs,
        category: formData.category,
        contextData: contextData,  // Add structured context data as a separate field
        systemInfo: systemInfo  // Also include system info as a top-level field for easier access
      };
      
      await logCollectionService.submitBug(bugReport)
      .then(result => {
        setSubmitStatus({ 
          success: true, 
          message: 'Thank you! Your bug report has been submitted successfully. Our team will look into it.' 
        });
        
        // Reset form
        setFormData({
          title: '',
          steps: '',
          includeLogs: true,
          category: '',
          folderName: '',
          parentFolder: '',
          googleDriveLink: '',
          languages: '',
          videoName: '',
          folderPath: ''
        });
        
        // Clear selected videos
        setSelectedVideo(null);
        setSelectedVideos([]);
        
        // Notify parent component if callback provided
        if (onSubmitSuccess && typeof onSubmitSuccess === 'function') {
          onSubmitSuccess(result);
        }
      })
      .catch (result => {
        setSubmitStatus({ success: false, message: result.message || 'Failed to submit bug report' });
      });
    } catch (error) {
      setSubmitStatus({ success: false, message: `Error: ${error.message}` });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Helper function to get category-specific fields
  const renderCategoryFields = () => {
    switch(formData.category) {
      case 'creating_folder':
        return (
          <Box sx={{ mt: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <FolderIcon fontSize="small" sx={{ mr: 1 }} /> 
              Folder Creation Details
            </Typography>
            <TextField
              fullWidth
              label="Folder Name"
              name="folderName"
              value={formData.folderName}
              onChange={handleChange}
              margin="normal"
              required
              size="small"
              helperText="Name of the folder you were trying to create"
              data-testid="folder-name-input"
            />
            <TextField
              fullWidth
              label="Parent Folder"
              name="parentFolder"
              value={formData.parentFolder}
              onChange={handleChange}
              margin="normal"
              required
              size="small"
              helperText="Where were you trying to create this folder"
              data-testid="parent-folder-input"
            />
          </Box>
        );
        
      case 'creating_video':
        return (
          <Box sx={{ mt: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <VideoLibraryIcon fontSize="small" sx={{ mr: 1 }} /> 
              Video Creation Details
            </Typography>
            <TextField
              fullWidth
              label="Google Drive Link"
              name="googleDriveLink"
              value={formData.googleDriveLink}
              onChange={handleChange}
              margin="normal"
              required
              size="small"
              helperText="Link to the video in Google Drive"
              data-testid="google-drive-link-input"
            />
            <TextField
              fullWidth
              label="Languages"
              name="languages"
              value={formData.languages}
              onChange={handleChange}
              margin="normal"
              required
              size="small"
              helperText="Which languages were you trying to create (e.g., English, Hindi, Gujarati)"
              data-testid="languages-input"
            />
          </Box>
        );
        
      case 'editing_video':
      case 'exporting_file':
      case 'importing_file':
      case 'saving_file':
        return (
          <Box sx={{ mt: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              {formData.category === 'editing_video' && <EditIcon fontSize="small" sx={{ mr: 1 }} />}
              {formData.category === 'exporting_file' && <ImportExportIcon fontSize="small" sx={{ mr: 1 }} />}
              {formData.category === 'importing_file' && <ImportExportIcon fontSize="small" sx={{ mr: 1 }} />}
              {formData.category === 'saving_file' && <SaveIcon fontSize="small" sx={{ mr: 1 }} />}
              Video Details
            </Typography>
            
            <Box sx={{ mb: 2 }}>
              <Button 
                variant="outlined" 
                color="primary" 
                onClick={handleOpenVideoBrowser}
                startIcon={<VideoLibraryIcon />}
                fullWidth
                sx={{ mb: 1 }}
                data-testid="browse-video-button"
              >
                {selectedVideo ? 'Change Selected Video' : 'Browse & Select Video'}
              </Button>
              
              {selectedVideo && (
                <Box>
                  {selectedVideos.length > 1 ? (
                    // Multiple videos selected
                    <>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="subtitle2">
                          {selectedVideos.length} videos selected
                        </Typography>
                        <IconButton 
                          size="small" 
                          onClick={() => {
                            setSelectedVideo(null);
                            setSelectedVideos([]);
                            setFormData({
                              ...formData,
                              videoName: '',
                              videoId: '',
                              folderPath: ''
                            });
                          }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Box>
                      
                      <Paper 
                        variant="outlined" 
                        sx={{ 
                          maxHeight: '200px', 
                          overflow: 'auto',
                          p: 1,
                          bgcolor: 'background.default'
                        }}
                      >
                        {selectedVideos.map((video, index) => (
                          <Box 
                            key={video._id || video.id || index}
                            sx={{ 
                              p: 1, 
                              borderBottom: index < selectedVideos.length - 1 ? '1px solid' : 'none',
                              borderColor: 'divider',
                              display: 'flex', 
                              alignItems: 'flex-start'
                            }}
                          >
                            <VideoLibraryIcon sx={{ mr: 1, color: 'primary.main', fontSize: '1rem', mt: 0.5 }} />
                            <Box sx={{ flexGrow: 1 }}>
                              <Typography variant="body2">{video.name}</Typography>
                              <Typography variant="caption" color="text.secondary" display="block">
                                <strong>Path:</strong> {video.formattedPath || formatPath(video.folderPath || video.path || '')}
                              </Typography>
                              {video.languages && (
                                <Typography variant="caption" color="text.secondary" display="block">
                                  <strong>Languages:</strong> {
                                    Array.isArray(video.languages) 
                                      ? video.languages.map(lang => typeof lang === 'string' ? lang : (lang.name || lang.languageName)).join(', ')
                                      : typeof video.languages === 'string'
                                        ? video.languages
                                        : 'Unknown'
                                  }
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        ))}
                      </Paper>
                    </>
                  ) : (
                    // Single video selected
                    <Paper 
                      variant="outlined" 
                      sx={{ 
                        p: 1.5, 
                        display: 'flex', 
                        alignItems: 'flex-start',
                        bgcolor: 'background.default'
                      }}
                      data-testid="selected-video-display"
                    >
                      <VideoLibraryIcon sx={{ mr: 1, color: 'primary.main', mt: 0.5 }} />
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="subtitle2">{selectedVideo.name}</Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          <strong>Path:</strong> {selectedVideo.formattedPath || formatPath(selectedVideo.folderPath || selectedVideo.path || '')}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          <strong>Video ID:</strong> {selectedVideo.videoId || selectedVideo.videoId || 'N/A'}
                        </Typography>
                        {selectedVideo.languages && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            <strong>Languages:</strong> {
                              Array.isArray(selectedVideo.languages) 
                                ? selectedVideo.languages.map(lang => typeof lang === 'string' ? lang : (lang.name || lang.languageName)).join(', ')
                                : typeof selectedVideo.languages === 'string'
                                  ? selectedVideo.languages
                                  : 'Unknown'
                            }
                          </Typography>
                        )}
                      </Box>
                      <IconButton 
                        size="small" 
                        onClick={() => {
                          setSelectedVideo(null);
                          setSelectedVideos([]);
                          setFormData({
                            ...formData,
                            videoName: '',
                            videoId: '',
                            folderPath: ''
                          });
                        }}
                        sx={{ mt: -0.5 }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Paper>
                  )}
                </Box>
              )}
            </Box>
            
            {!selectedVideo && (
              <>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                  <TextField
                    fullWidth
                    label="Video Name"
                    name="videoName"
                    value={formData.videoName}
                    onChange={handleChange}
                    margin="normal"
                    required
                    size="small"
                    helperText="Name of the video you were working with"
                    data-testid="video-name-input"
                  />
                </Box>
                
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                  <TextField
                    fullWidth
                    label="Folder Path"
                    name="folderPath"
                    value={formData.folderPath}
                    onChange={handleChange}
                    margin="normal"
                    required
                    size="small"
                    helperText="Which folder contains this video"
                    data-testid="folder-path-input"
                  />
                  
                  
                </Box>
              </>
            )}
            
            <Box sx={{ my: 2 }} data-testid="language-selection-container">
              {selectedVideo ? (
                // If a video is selected, show its languages or allow selection from its available languages
                <>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                    Languages for this video:
                  </Typography>
                  {selectedVideo.videoLanguages?.length > 0 ? (
                    <SelectLanguageComponent
                      id="selectLanguageDropdown"
                      disabled={false}
                      // Use the languages from the selected video
                      languageOptions={() => selectedVideo.videoLanguages.map(lang => lang.languageName)}
                      value={formData.languages}
                      onChange={(value) => {
                        setFormData({
                          ...formData,
                          languages: value
                        });
                      }}
                      data-testid="languages-input"
                    />
                  ) : (
                    <TextField
                      fullWidth
                      label="Languages"
                      name="languages"
                      value={formData.languages}
                      onChange={handleChange}
                      margin="normal"
                      required
                      size="small"
                      helperText="Enter languages separated by commas"
                      data-testid="languages-input"
                    />
                  )}
                </>
              ) : (
                // If no video is selected, show the default language selection
                <TextField
                  fullWidth
                  label="Languages"
                  name="languages"
                  value={formData.languages}
                  onChange={handleChange}
                  margin="normal"
                  required
                  size="small"
                  helperText="Which languages were you working with (e.g., English, Hindi)"
                  data-testid="languages-input"
                />
              )}
            </Box>
          </Box>
        );
        
      default:
        return null;
    }
  };

  const handleOpenBrowser = () => {
    setOpenBrowserDialog(true);
  };

  const handleCloseBrowser = () => {
    setOpenBrowserDialog(false);
  };

  const handleSelectVideo = (video) => {
    setSelectedVideo(video);
    // Update form data with selected video info
    setFormData({
      ...formData,
      videoName: video.name,
      videoId: video._id,
      folderPath: selectedPath
    });
    setOpenBrowserDialog(false);
  };

  const handleOpenVideoBrowser = () => {
    setVideoBrowserOpen(true);
  };

  const handleVideoSelected = (videos) => {
    // Check if we received an array of videos
    if (Array.isArray(videos)) {
      if (videos.length > 0) {
        // Store all selected videos
        setSelectedVideos(videos);
        
        // Use the first video for displaying in the form
        const firstVideo = videos[0];
        setSelectedVideo(firstVideo);
        
        // Extract languages from all selected videos - FIX THE LANGUAGE EXTRACTION
        let allLanguages = '';
        
        // Try different possible language formats
        if (videos.some(v => v.languages && Array.isArray(v.languages))) {
          // If languages is an array of language objects with name property
          allLanguages = videos
            .flatMap(video => video.languages || [])
            .filter(lang => lang && (typeof lang === 'string' || lang.name || lang.languageName))
            .map(lang => typeof lang === 'string' ? lang : (lang.name || lang.languageName))
            .filter((value, index, self) => self.indexOf(value) === index) // unique values
            .join(', ');
        } else if (videos.some(v => v.videoLanguages && Array.isArray(v.videoLanguages))) {
          // If videoLanguages is available (alternative property name)
          allLanguages = videos
            .flatMap(video => video.videoLanguages || [])
            .filter(lang => lang && (typeof lang === 'string' || lang.name || lang.languageName))
            .map(lang => typeof lang === 'string' ? lang : (lang.name || lang.languageName))
            .filter((value, index, self) => self.indexOf(value) === index)
            .join(', ');
        }
        
        console.log('Extracted languages:', allLanguages);
        
        // Update form data
        setFormData({
          ...formData,
          videoName: videos.length === 1 
            ? firstVideo.name 
            : `${videos.length} videos selected`,
          videoId: firstVideo.videoId || firstVideo._id || firstVideo.id || '',
          folderPath: firstVideo.folderPath || firstVideo.path || '',
          languages: allLanguages || formData.languages || 'Unknown'  // Ensure we always have a value
        });
      }
    } else if (videos) {
      // Single video case - maintain backward compatibility
      setSelectedVideo(videos);
      setSelectedVideos([videos]); // Also store as array for consistency
    
      // Determine languages from the video
      let languages = '';
      
      // Try different possible language formats
      if (videos.languages && Array.isArray(videos.languages)) {
        languages = videos.languages
          .filter(lang => lang && (typeof lang === 'string' || lang.name || lang.languageName))
          .map(lang => typeof lang === 'string' ? lang : (lang.name || lang.languageName))
          .join(', ');
      } else if (videos.videoLanguages && Array.isArray(videos.videoLanguages)) {
        languages = videos.videoLanguages
          .filter(lang => lang && (typeof lang === 'string' || lang.name || lang.languageName))
          .map(lang => typeof lang === 'string' ? lang : (lang.name || lang.languageName))
          .join(', ');
      }
      
      console.log('Single video languages:', languages);
      
      setFormData({
        ...formData,
        videoName: videos.name,
        videoId: videos.videoId || videos._id || videos.id || '',
        folderPath: videos.folderPath || videos.path || '',
        languages: languages || formData.languages || 'Unknown'  // Ensure we always have a value
      });
    }
  
    setVideoBrowserOpen(false);
  };

  const formatPath = (path) => {
    if (!path) return 'Home';
    if (path === '/') return 'Home';
    return path;
  };

  // Add system information capture to bug reports

  // First, add a function to collect system information
  const getSystemInfo = () => {
    try {
      const systemInfo = {
        // Browser information
        userAgent: navigator.userAgent,
        // Screen resolution
        screenWidth: window.screen.width,
        screenHeight: window.screen.height,
        windowWidth: window.innerWidth,
        windowHeight: window.innerHeight,
        pixelRatio: window.devicePixelRatio,
        // Platform/OS
        platform: navigator.platform,
        // Browser language
        language: navigator.language,
        // Browser capabilities
        cookiesEnabled: navigator.cookieEnabled,
        // Date and time
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        timestamp: new Date().toISOString()
      };
      
      // Try to detect OS more precisely
      const userAgent = navigator.userAgent;
      if (userAgent.indexOf('Win') !== -1) systemInfo.os = 'Windows';
      else if (userAgent.indexOf('Mac') !== -1) systemInfo.os = 'MacOS';
      else if (userAgent.indexOf('Linux') !== -1) systemInfo.os = 'Linux';
      else if (userAgent.indexOf('Android') !== -1) systemInfo.os = 'Android';
      else if (userAgent.indexOf('iOS') !== -1 || userAgent.indexOf('iPhone') !== -1 || userAgent.indexOf('iPad') !== -1) 
        systemInfo.os = 'iOS';
      else systemInfo.os = 'Unknown';
      
      return systemInfo;
    } catch (error) {
      console.error('Error collecting system info:', error);
      return { error: 'Failed to collect system information' };
    }
  };

  // Load prefilled data if available
    React.useEffect(() => {
    // Check for pre-filled data in localStorage
    const prefillData = localStorage.getItem('bugReportPrefill');
    
    if (prefillData) {
        try {
        const parsedData = JSON.parse(prefillData);
        
        // Update form data with prefilled values
        setFormData(prevData => ({
            ...prevData,
            ...parsedData
        }));
        
        // Clear the prefill data to avoid reusing it
        localStorage.removeItem('bugReportPrefill');
        } catch (error) {
        console.error('Error parsing prefilled bug report data:', error);
        }
    }
    }, []);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <BugReportIcon sx={{ mr: 1, color: theme => theme.palette.error.main }} />
          <Typography variant="subtitle1" fontWeight="medium">
            Submit Bug Report
          </Typography>
        </Box>
      </Box>

      <form onSubmit={handleSubmit} noValidate data-testid="bug-report-form">
        <FormControl fullWidth margin="normal" required>
          <InputLabel id="bug-category-label">Issue Category</InputLabel>
          <Select
            labelId="bug-category-label"
            id="bug-category"
            name="category"
            value={formData.category}
            onChange={handleChange}
            label="Issue Category"
            size="small"
            data-testid="bug-category-select"
          >
            {bugCategories.map((category) => (
              <MenuItem key={category.value} value={category.value} data-testid={`category-option-${category.value}`}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {category.icon}
                  <span style={{ marginLeft: 8 }}>{category.label}</span>
                </Box>
              </MenuItem>
            ))}
          </Select>
          <FormHelperText>Select the category that best describes your issue</FormHelperText>
        </FormControl>
        
        <TextField
          fullWidth
          label="Title"
          name="title"
          value={formData.title}
          onChange={handleChange}
          margin="normal"
          required
          size="small"
          placeholder="Brief summary of the issue"
          data-testid="bug-title-input"
        />
        
        {/* Render category-specific fields */}
        <Collapse in={!!formData.category}>
          {renderCategoryFields()}
        </Collapse>
        
        <TextField
          fullWidth
          label="Steps to Reproduce"
          name="steps"
          value={formData.steps}
          onChange={handleChange}
          margin="normal"
          multiline
          rows={2}
          size="small"
          helperText="List the steps to reproduce this issue"
          data-testid="steps-input"
        />
        
        <FormControlLabel
          control={
            <Checkbox
              checked={formData.includeLogs}
              onChange={handleChange}
              name="includeLogs"
              data-testid="include-logs-checkbox"
            />
          }
          label="Include browser logs (recommended for better diagnosis)"
        />
        
        {submitStatus && (
          <Alert 
            severity={submitStatus.success ? "success" : "error"} 
            sx={{ mt: 2, mb: 2 }}
            onClose={() => setSubmitStatus(null)}
            data-testid="submit-status-alert"
          >
            {submitStatus.message}
          </Alert>
        )}
        
        {/* System information display */}
        <Box sx={{ mt: 2, mb: 2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            The following system information will be included in your report:
          </Typography>
          
          <Paper 
            variant="outlined" 
            sx={{ 
              p: 1.5, 
              backgroundColor: theme => 
                theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
              borderRadius: 1,
              fontSize: '0.75rem',
              fontFamily: 'monospace',
            }}
          >
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              <Box>
                <Typography variant="caption" fontWeight="bold" display="block">OS:</Typography>
                <Typography variant="caption">{getSystemInfo().os || getSystemInfo().platform}</Typography>
              </Box>
              
              <Box>
                <Typography variant="caption" fontWeight="bold" display="block">Screen Resolution:</Typography>
                <Typography variant="caption">
                  {getSystemInfo().screenWidth}x{getSystemInfo().screenHeight} ({getSystemInfo().pixelRatio}x)
                </Typography>
              </Box>
              
              <Box>
                <Typography variant="caption" fontWeight="bold" display="block">Browser:</Typography>
                <Typography variant="caption" sx={{ maxWidth: '200px', display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {getSystemInfo().userAgent}
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Box>
        
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
            disabled={isSubmitting}
            data-testid="submit-report-button"
          >
            {isSubmitting ? 'Submitting...' : 'Submit Report'}
          </Button>
        </Box>
      </form>
      
      {/* Video browser dialog with integrated path navigation */}
      <VideoBrowserDialog
        open={videoBrowserOpen}
        onClose={() => setVideoBrowserOpen(false)}
        onSelect={handleVideoSelected}
        title="Select Videos for Bug Report"
        multiSelect={true}  // Enable multi-select
        calledFrom="bugReport"  // Identify the caller
      />
    </Box>
  );
};

export default BugReportForm;